import { testsApi, aiAPI } from '@edulastic/api'
import { all, call, put, takeEvery } from 'redux-saga/effects'
import { createSelector } from 'reselect'
import { notification } from '@edulastic/common'
import { alignmentStandardsFromUIToMongo } from '../../../../assessment/utils/helpers'
import { DEFAULT_TEST_TITLE } from '../../../TestPage/utils'
import { getAlignmentDataForAiQuestions } from '../CreateAITest/ducks/helpers'
import { updateMetaData } from './utils'
import { AI_ERROR_MAP } from '../../../AIFeatures/constants/form'

export const namespaceKey = 'importTestFromDocReducer'

// actions constants
export const IMPORT_TEST_FROM_DOC = `[test from doc] import test from doc`
export const SET_IMPORT_TEST_FROM_DOC = `[test from doc] set state`
export const PROCESS_IMPORTED_QUESTIONS_AND_CREATE_TEST = `[test from doc] process imported questions and create test`
export const DELETE_IMPORTED_TEST = `[test from doc] delete imported test`

// actions
export const importTestFromDocAction = (payload) => ({
  type: IMPORT_TEST_FROM_DOC,
  payload,
})

export const setImportTestFromDocStateAction = (payload) => ({
  type: SET_IMPORT_TEST_FROM_DOC,
  payload,
})

export const processImportedQuestionsAndCreateTestAction = (payload) => ({
  type: PROCESS_IMPORTED_QUESTIONS_AND_CREATE_TEST,
  payload,
})

export const deleteImportedTestAction = (payload) => ({
  type: DELETE_IMPORTED_TEST,
  payload,
})

// selectors
export const stateSelector = (state) => state?.[namespaceKey]

export const getImportTestFromDocSelector = createSelector(
  stateSelector,
  (state) => state || {}
)

// initial state
const initialState = {
  apiStatus: false,
  result: {},
  errorMessage: '',
  deleteApiStatus: false,
}

// reducer
export const reducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case SET_IMPORT_TEST_FROM_DOC:
      return {
        ...state,
        ...payload,
      }
    default:
      return state
  }
}

// sagas
function* importTestFromDocSaga({ payload }) {
  const generalErrorMessage = 'Something went wrong. Please try again'
  try {
    yield put(
      setImportTestFromDocStateAction({
        apiStatus: 'INITIATED',
        result: [],
        errorMessage: '',
      })
    )
    const {
      cdnUrl,
      alignment: { curriculum = '', grades, subject, standards = [] } = {},
      fileName = DEFAULT_TEST_TITLE,
    } = payload || {}
    let commonCoreStandards = []
    let commonCoresStandardDescriptions = []

    if (standards?.length) {
      const formattedAlignmentData = getAlignmentDataForAiQuestions([
        {
          grades,
          subject,
          standards,
          curriculum,
        },
      ])
      commonCoreStandards = formattedAlignmentData?.standardIds || []
      commonCoresStandardDescriptions =
        formattedAlignmentData?.standardDescriptions || []
    }

    const alignment = payload.alignment
      ? {
          ...payload.alignment,
          domains: alignmentStandardsFromUIToMongo(standards),
        }
      : undefined

    const importQuestionPayload = {
      fileName,
      url: cdnUrl,
      subject,
      grades,
      standardSet: curriculum,
      commonCoreStandards,
      commonCoresStandardDescriptions,
      alignment,
    }
    let { result } = yield call(aiAPI.importQuestion, importQuestionPayload)
    const updatedMetaData = updateMetaData(result)
    result = { ...result, metaData: updatedMetaData }
    yield put(
      setImportTestFromDocStateAction({
        apiStatus: 'SUCCESS',
        result,
        errorMessage: '',
      })
    )
  } catch (err) {
    const errMsg = err?.response?.data?.message
    const aiErrorMessage = AI_ERROR_MAP?.[errMsg]
    if (errMsg) {
      notification({
        type: 'error',
        exact: !!aiErrorMessage,
        msg: aiErrorMessage || errMsg,
      })
    } else {
      notification({
        type: 'error',
        messageKey: 'aiImportQuestionsFailed',
      })
    }

    yield put(
      setImportTestFromDocStateAction({
        apiStatus: 'FAILED',
        result: [],
        errorMessage: err?.message || generalErrorMessage,
      })
    )
  }
}

function* deleteImportedTestSaga({ payload }) {
  const { _id: testId } = payload
  try {
    yield put(
      setImportTestFromDocStateAction({
        deleteApiStatus: 'INITIATED',
      })
    )
    yield call(testsApi.deleteTest, { testId })

    yield put(
      setImportTestFromDocStateAction({
        deleteApiStatus: 'SUCCESS',
      })
    )
  } catch (err) {
    console.error(err)
    const errMsg = err?.response?.data?.message
    if (errMsg) {
      notification({
        type: 'error',
        msg: errMsg,
      })
    } else {
      notification({
        type: 'error',
        msg: 'Failed to discard the Test.',
      })
    }

    yield put(
      setImportTestFromDocStateAction({
        deleteApiStatus: 'FAILED',
      })
    )
  }
}

export function* watcherSaga() {
  yield all([
    takeEvery(IMPORT_TEST_FROM_DOC, importTestFromDocSaga),
    takeEvery(DELETE_IMPORTED_TEST, deleteImportedTestSaga),
  ])
}
